import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import type { Pointer, ScrollTarget } from "./types";
import {
    convertToElementScrollValue,
    getElementScrollValueRelative,
    getEventPointer,
    getHandleOffsetX,
    getHandleOffsetY
} from "./scrollbar-utils";

enum ControlIdentifier {
    STEP_UP = "step-up",
    STEP_DOWN = "step-down",
    PAGE_UP = "page-up",
    PAGE_DOWN = "page-down",
    PAGE_START = "page-start",
    PAGE_END = "page-end"
}

enum ScrollDirection {
    Horizontal = "horizontal",
    Vertical = "vertical"
}

@Component({
    tag: "ux-scrollbar",
    styleUrl: "scrollbar.scss",
    shadow: true
})
export class UxScrollbar {
    @Element() host: HTMLUxScrollbarElement;

    /** Set to `true` if the scrollbar should display additional controls */
    @Prop() readonly controls: boolean = false;

    /** The scroll direction of the scrollbar */
    @Prop({ reflect: true }) readonly direction: "horizontal" | "vertical" = "vertical";

    /** The maximum scroll amount in pixels if used without connected element */
    @Prop() readonly scrollsize: number = 0;

    /** The page scroll offset in pixels if used without connected element */
    @Prop() readonly pagesize: number = 0;

    /** Scroll amount used when clicking a step button */
    @Prop() readonly stepsize: number = 40;

    /** ID of the element that should be connected to the scrollbar */
    @Prop() readonly connectedId: string = "";

    /** Native scroll behavior */
    @Prop() readonly scrollBehavior: ScrollBehavior = "smooth";

    /** The connected scroll element */
    @State() connectedElement: HTMLElement | ScrollTarget | null = null;

    /** Indicates if the scrollbar has reached the start limit */
    @State() startReached = true;

    /** Indicates if the scrollbar has reached the end limit */
    @State() endReached = false;

    /** Update UI when the viewport is resized */
    @Listen("resize", { target: "window" })
    handleResize() {
        this.updateHandleDebounced();
    }

    /** Emitted when the scrollbar was dragged or a scrollbar control was clicked */
    @Event() uxScroll: EventEmitter<{ horizontal?: number; vertical?: number }>;

    /** Emitted when a scroll limit is reached */
    @Event() uxScrollEnd: EventEmitter<"top" | "bottom" | "left" | "right">;

    /** Emitted when a step button was clicked */
    @Event() uxStep: EventEmitter<"up" | "down">;

    /** Emitted when a page button was clicked */
    @Event() uxPage: EventEmitter<"up" | "down">;

    /** Connect an element that should be controlled by this scrollbar */
    @Method()
    async connect(target: HTMLElement): Promise<void> {
        if (this.connectedElement !== null) {
            await this.disconnect();
        }
        this.connectedElement = target;
        if (this.connectedElement instanceof HTMLElement) {
            this.connectedElement.addEventListener("scroll", this.handleConnectedElementScroll);
        }
        this.handleConnectedElementScroll();
    }

    /** Disconnect the connected element */
    @Method()
    async disconnect(): Promise<void> {
        if (this.connectedElement instanceof HTMLElement) {
            this.connectedElement.removeEventListener("scroll", this.handleConnectedElementScroll);
        }
        this.connectedElement = null;
    }

    private getScrollbars = (): HTMLUxScrollbarElement[] => {
        return this.host.parentElement ? Array.from(this.host.parentElement.querySelectorAll("ux-scrollbar")) : [];
    };

    async componentWillLoad() {
        if (this.connectedId) {
            const target = document.getElementById(this.connectedId);
            if (target !== null) {
                await this.connect(target);
            }
        }
    }

    async componentDidLoad() {
        this.updateHandleDebounced();
    }

    async disconnectedCallback() {
        this.removeDocumentListeners();
        await this.disconnect();
    }

    /** Local scroll value (0-1) */
    private scrollValue = 0;
    /** Indicates if the handle is currently dragged */
    private isDragging = false;
    /** Storage for the handle drag start pointer */
    private dragStartPointer: Pointer;
    /** Storage for the handle drag start offset in pixels */
    private dragStartOffset = 0;
    /** Storage for the handle drag offset while dragging in pixels */
    private dragOffset = 0;

    /** Indicates that a second scrollbar exists which controls the other axis */
    private get hasOtherScrollbar(): boolean {
        return this.getScrollbars().length > 1;
    }

    /** Remove drag event listeners */
    private removeDocumentListeners = () => {
        document.removeEventListener("mousemove", this.handleDragMove);
        document.removeEventListener("mouseup", this.handleDragEnd);
        document.removeEventListener("touchmove", this.handleDragMove);
        document.removeEventListener("touchend", this.handleDragEnd);
    };

    /** Add drag event listeners */
    private addDocumentListeners = () => {
        this.removeDocumentListeners();
        document.addEventListener("mousemove", this.handleDragMove);
        document.addEventListener("mouseup", this.handleDragEnd);
        document.addEventListener("touchmove", this.handleDragMove);
        document.addEventListener("touchend", this.handleDragEnd);
    };

    /** The bar `HTMLElement` */
    private get barElement(): HTMLElement | null | undefined {
        return this.host.shadowRoot?.querySelector<HTMLDivElement>(".bar");
    }

    /** The handle `HTMLElement` */
    private get handleElement(): HTMLElement | null | undefined {
        return this.host.shadowRoot?.querySelector<HTMLDivElement>(".handle");
    }

    private emitUxScrollEvent = () => {
        const scrollValueFixed = Math.max(0, Math.min(1, this.scrollValue));
        if (this.direction === ScrollDirection.Horizontal) {
            this.uxScroll.emit({
                horizontal: scrollValueFixed
            });
        } else {
            this.uxScroll.emit({
                vertical: scrollValueFixed
            });
        }
    };

    /** Update handle size and position */
    private updateHandle = () => {
        if (!this.barElement || !this.handleElement) {
            return;
        }
        const maxOffset =
            this.direction === ScrollDirection.Horizontal
                ? this.barElement.clientWidth - this.handleElement.clientWidth
                : this.barElement.clientHeight - this.handleElement.clientHeight;
        const handleRatio =
            this.direction === ScrollDirection.Horizontal
                ? maxOffset / this.barElement.clientWidth
                : maxOffset / this.barElement.clientHeight;
        const target = this.connectedElement;
        let visibleRatio = 0;
        let handleOffsetPercentage = 0;
        if (target) {
            const scrollValueRelative = getElementScrollValueRelative(target);
            visibleRatio =
                this.direction === ScrollDirection.Horizontal
                    ? target.clientWidth / target.scrollWidth
                    : target.clientHeight / target.scrollHeight;
            if (this.direction === ScrollDirection.Horizontal) {
                if (!this.isDragging) {
                    this.scrollValue = scrollValueRelative.left;
                }
            } else {
                if (!this.isDragging) {
                    this.scrollValue = scrollValueRelative.top;
                }
            }
            handleOffsetPercentage = this.scrollValue * handleRatio * 100;
        } else {
            handleOffsetPercentage = this.scrollValue * handleRatio * 100;
        }
        if (!this.isDragging) {
            this.updateDragStartOffset();
        }

        this.handleElement.style.setProperty("--handle-length", visibleRatio > 0 ? `${visibleRatio * 100}%` : "unset");
        this.handleElement.style.setProperty("--handle-offset", `${handleOffsetPercentage}%`);
    };

    /** Debounced instance of `updateHandle()` */
    private updateHandleDebounced = debounce(this.updateHandle, 50);

    /** Update scroll position of the connected element directly */
    private updateConnectedElement = () => {
        if (this.connectedElement) {
            if (this.direction === ScrollDirection.Horizontal) {
                this.connectedElement.scrollLeft =
                    this.scrollValue * (this.connectedElement.scrollWidth - this.connectedElement.clientWidth);
            } else {
                this.connectedElement.scrollTop =
                    this.scrollValue * (this.connectedElement.scrollHeight - this.connectedElement.clientHeight);
            }
        }
    };

    /** Update scroll position of the connected element via native `scrollTo` function  */
    private scrollConnectedElement = () => {
        if (typeof this.connectedElement?.scrollTo === "function") {
            const targetScrollValue = convertToElementScrollValue(this.scrollValue, this.connectedElement, this.direction);
            this.connectedElement.scrollTo({
                ...targetScrollValue,
                behavior: this.scrollBehavior
            });
        } else {
            this.updateConnectedElement();
        }
    };

    /** Update the local drag start handle offset */
    private updateDragStartOffset = () => {
        if (this.barElement && this.handleElement) {
            this.dragStartOffset =
                this.direction === ScrollDirection.Horizontal
                    ? getHandleOffsetX(this.barElement, this.handleElement, this.scrollValue)
                    : getHandleOffsetY(this.barElement, this.handleElement, this.scrollValue);
        }
    };

    /** Update the states indicating that scroll limits are reached */
    private updateLimitsReached = () => {
        const startReached = this.scrollValue <= 0;
        const endReached = this.scrollValue >= 1;
        if (startReached !== this.startReached) {
            this.startReached = startReached;
            if (startReached) {
                this.uxScrollEnd.emit(this.direction === ScrollDirection.Horizontal ? "left" : "top");
            }
        }
        if (endReached !== this.endReached) {
            this.endReached = endReached;
            if (endReached) {
                this.uxScrollEnd.emit(this.direction === ScrollDirection.Horizontal ? "right" : "bottom");
            }
        }
    };

    /** Update the `aria-valuenow` attribute */
    private updateAriaAttributes = () => {
        this.host.shadowRoot?.querySelector(`.bar`)?.setAttribute("aria-valuenow", `${this.scrollValue}`);
    };

    /** Returns `true` if the pointer hits the handle, otherwise `false` */
    private getPointerHitsHandle = (p: Pointer): boolean => {
        if (!this.handleElement) {
            return false;
        }
        const rect = this.handleElement.getBoundingClientRect();
        return this.direction === ScrollDirection.Horizontal
            ? p.x >= rect.x && p.x <= rect.x + rect.width
            : p.y >= rect.y && p.y <= rect.y + rect.height;
    };

    /** Returns the `scrollValue` for given pointer coordinates in relation to the bar element  */
    private getBarPointerScrollValue = (p: Pointer): number => {
        let scrollValue = this.scrollValue;
        if (this.barElement && this.handleElement) {
            const rect = this.barElement.getBoundingClientRect();
            const offset =
                this.direction === ScrollDirection.Horizontal
                    ? p.x - rect.x - 0.5 * this.handleElement.clientWidth
                    : p.y - rect.y - 0.5 * this.handleElement.clientHeight;
            const maxOffset =
                this.direction === ScrollDirection.Horizontal
                    ? this.barElement.clientWidth - this.handleElement.clientWidth
                    : this.barElement.clientHeight - this.handleElement.clientHeight;
            scrollValue = maxOffset === 0 ? 0 : offset / maxOffset;
        }
        return Math.max(0, Math.min(1, scrollValue));
    };

    /** Returns the calculated page scroll amount of an element or scroll target  */
    private getElementPageScroll = (target: HTMLElement | ScrollTarget): number => {
        if (!target) {
            return 0;
        }
        return this.direction === ScrollDirection.Horizontal ? target.clientWidth : target.clientHeight;
    };

    /** Returns the calculated max scroll amount of an element or scroll target  */
    private getElementMaxScroll = (target: HTMLElement | ScrollTarget): number => {
        if (!target) {
            return 0;
        }
        return this.direction === ScrollDirection.Horizontal
            ? target.scrollWidth - target.clientWidth
            : target.scrollHeight - target.clientHeight;
    };

    /** Handle drag end event */
    private handleDragEnd = () => {
        this.removeDocumentListeners();
        this.isDragging = false;
        this.dragStartOffset = this.dragOffset;
        document.documentElement.style.cursor = "auto";
    };

    /** Handle drag start event */
    private handleDragStart = (e: unknown) => {
        if (!this.barElement || !this.handleElement) {
            return;
        }
        const event = e as MouseEvent | TouchEvent;
        event.preventDefault();
        const pointer = getEventPointer(event);
        this.isDragging = true;
        const lastScrollValue = this.scrollValue;
        if (!this.getPointerHitsHandle(pointer)) {
            this.scrollValue = this.getBarPointerScrollValue(pointer);
            if (event.altKey || !this.connectedElement) {
                this.updateConnectedElement();
                this.updateHandle();
                this.updateDragStartOffset();
                if (this.scrollValue !== lastScrollValue) {
                    this.updateAriaAttributes();
                    this.emitUxScrollEvent();
                }
            } else if (this.connectedElement) {
                this.scrollConnectedElement();
                this.isDragging = false;
                return;
            }
        }
        this.dragOffset = this.dragStartOffset;
        this.dragStartPointer = pointer;
        document.documentElement.style.cursor = "grabbing";
        this.addDocumentListeners();
    };

    /** Handle drag move event */
    private handleDragMove = (event: MouseEvent | TouchEvent) => {
        if (!this.isDragging) {
            return;
        }
        if (!this.barElement || !this.handleElement) {
            return;
        }
        const pointer = getEventPointer(event);
        const lastScrollValue = this.scrollValue;
        const dragOffset =
            this.direction === ScrollDirection.Horizontal
                ? this.dragStartOffset + pointer.x - this.dragStartPointer.x
                : this.dragStartOffset + pointer.y - this.dragStartPointer.y;
        const maxOffset =
            this.direction === ScrollDirection.Horizontal
                ? this.barElement.clientWidth - this.handleElement.clientWidth
                : this.barElement.clientHeight - this.handleElement.clientHeight;
        const handleOffset = Math.max(0, Math.min(dragOffset, maxOffset));
        this.scrollValue = Math.max(0, Math.min(1, handleOffset / maxOffset));
        this.dragOffset = handleOffset;
        this.updateHandle();
        this.updateConnectedElement();
        if (this.scrollValue !== lastScrollValue) {
            this.updateAriaAttributes();
            this.emitUxScrollEvent();
        }
        this.updateLimitsReached();
    };

    /** Handle scroll event emitted by the connected element */
    private handleConnectedElementScroll = () => {
        this.updateHandle();
        this.updateLimitsReached();
        this.updateAriaAttributes();
    };

    private handleControl = (controlId: string) => {
        if (!controlId) {
            return;
        }
        const maxscroll = this.connectedElement ? this.getElementMaxScroll(this.connectedElement) : this.scrollsize;
        const pagesize = this.connectedElement ? this.getElementPageScroll(this.connectedElement) : this.pagesize;
        if (maxscroll === 0) {
            return;
        }
        let scrollValue = this.scrollValue;
        switch (controlId) {
            case ControlIdentifier.STEP_UP:
                scrollValue = Math.max(0, scrollValue - this.stepsize / maxscroll);
                this.uxStep.emit("up");
                break;
            case ControlIdentifier.STEP_DOWN:
                scrollValue = Math.min(1, scrollValue + this.stepsize / maxscroll);
                this.uxStep.emit("down");
                break;
            case ControlIdentifier.PAGE_UP:
                scrollValue = Math.max(0, scrollValue - pagesize / maxscroll);
                this.uxPage.emit("up");
                break;
            case ControlIdentifier.PAGE_DOWN:
                scrollValue = Math.min(1, scrollValue + pagesize / maxscroll);
                this.uxPage.emit("down");
                break;
            case ControlIdentifier.PAGE_START:
                scrollValue = 0;
                break;
            case ControlIdentifier.PAGE_END:
                scrollValue = 1;
                break;
            default:
                return;
        }
        if (scrollValue !== this.scrollValue) {
            this.scrollValue = scrollValue;
            this.updateAriaAttributes();
            this.emitUxScrollEvent();
            this.scrollConnectedElement();
            this.updateHandle();
            this.updateLimitsReached();
        }
    };

    private getEventControlId = (e: unknown): string => {
        const event = e as UIEvent;
        return (event.target as HTMLElement)?.closest("[data-control]")?.getAttribute("data-control") || "";
    };

    /** Handle control events */
    private handleControlEvent = (e: unknown) => {
        const event = e as MouseEvent | TouchEvent | KeyboardEvent;
        if (event.type === "keyPress" && (event as KeyboardEvent).key !== "Enter") {
            return;
        }
        const controlId = this.getEventControlId(event);
        if (controlId) {
            this.handleControl(controlId);
        }
    };

    private handleInnerClick = (event: globalThis.Event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
    };

    render() {
        const maxscroll = this.connectedElement ? this.getElementMaxScroll(this.connectedElement) : this.scrollsize;
        const pagesize = this.connectedElement ? this.getElementPageScroll(this.connectedElement) : this.pagesize;
        return (
            <Host class={this.hasOtherScrollbar ? "ux-scrollbar--multiple" : "ux-scrollbar--single"}>
                <div class="inner" onClick={this.handleInnerClick}>
                    {this.controls && (
                        <div
                            class={{
                                ["controls"]: true,
                                ["controls--start"]: true,
                                ["controls--disabled"]: this.startReached
                            }}
                            onMouseDown={this.handleControlEvent}
                            onTouchStart={this.handleControlEvent}
                            onKeyPress={this.handleControlEvent}
                        >
                            <button class="btn" data-control={ControlIdentifier.PAGE_START} disabled={this.startReached}>
                                <ux-icon name="chevron-scroll-down-end" />
                            </button>
                            {pagesize > 0 && pagesize < maxscroll && (
                                <button class="btn" data-control={ControlIdentifier.PAGE_UP} disabled={this.startReached}>
                                    <ux-icon name="chevron-scroll-down-double" />
                                </button>
                            )}
                            <button class="btn" data-control={ControlIdentifier.STEP_UP} disabled={this.startReached}>
                                <ux-icon name="chevron-scroll-down" />
                            </button>
                        </div>
                    )}
                    <div class="controls controls--middle">
                        <div
                            class="bar"
                            role="scrollbar"
                            aria-valuenow={this.scrollValue}
                            aria-valuemin={0}
                            aria-valuemax={1}
                            onMouseDown={this.handleDragStart}
                            onTouchStart={this.handleDragStart}
                        >
                            <div class="track" />
                            <div class="handle" />
                        </div>
                    </div>
                    {this.controls && (
                        <div
                            class={{
                                ["controls"]: true,
                                ["controls--end"]: true,
                                ["controls--disabled"]: this.endReached
                            }}
                            onMouseDown={this.handleControlEvent}
                            onTouchStart={this.handleControlEvent}
                            onKeyPress={this.handleControlEvent}
                        >
                            <button class="btn" data-control={ControlIdentifier.STEP_DOWN} disabled={this.endReached}>
                                <ux-icon name="chevron-scroll-down" weight="bold" />
                            </button>
                            {pagesize > 0 && pagesize < maxscroll && (
                                <button class="btn" data-control={ControlIdentifier.PAGE_DOWN} disabled={this.endReached}>
                                    <ux-icon name="chevron-scroll-down-double" />
                                </button>
                            )}
                            <button class="btn" data-control={ControlIdentifier.PAGE_END} disabled={this.endReached}>
                                <ux-icon name="chevron-scroll-down-end" />
                            </button>
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
